import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple cache for home page data - stores only essential fields
const HOME_CACHE_KEY = 'cloudgm_home_cache_v3';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// -------- Data normalisation --------------------------------------------------
// Coerce price==0 / null with no salePrice into a "priceOnRequest" item so the
// UI can render the neutral "Contact for price" pill instead of "0 AED".
const normalizeItem = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;
  const item = { ...raw };
  const price = item.price;
  const sale  = item.salePrice;
  const rent  = item.rent_price;
  const emptyPrice = (price == null || price === 0 || price === '0') && !sale && !rent;
  if (emptyPrice && !item.priceOnRequest) {
    item.priceOnRequest = true;
  }
  return item;
};
const normalizeArray = (arr) => (Array.isArray(arr) ? arr.map(normalizeItem) : []);

// Keep an item in the "offers/discounts" section ONLY when it has an actual
// discount: hasDiscount===true AND a salePrice strictly less than the original.
const isRealDiscount = (i) => {
  if (!i || i.priceOnRequest) return false;
  if (i.hasDiscount !== true) return false;
  const sale = Number(i.salePrice);
  const orig = Number(i.originalPrice ?? i.price);
  return Number.isFinite(sale) && Number.isFinite(orig) && sale > 0 && sale < orig;
};

// Extract only essential fields to minimize cache size
const extractLiteItem = (item) => ({
  id: item.id,
  name: item.name,
  nameEn: item.nameEn,
  price: item.price,
  salePrice: item.salePrice,
  image: item.images?.[0] || item.image || '', // Only first image
  item_type: item.item_type,
  store_id: item.store_id,
  store_type: item.store_type,
  // For phone numbers
  number: item.number,
  operator: item.operator,
  // For cars
  brand: item.brand,
  model: item.model,
  year: item.year,
  // For real estate
  type: item.type,
  bedrooms: item.bedrooms,
  bathrooms: item.bathrooms,
  area: item.area,
  listing_type: item.listing_type,
  // For plates
  emirate: item.emirate,
  code: item.code,
  plate_number: item.plate_number
});

const extractLiteArray = (arr) => (arr || []).slice(0, 10).map(extractLiteItem);

const getCache = () => {
  try {
    const cached = localStorage.getItem(HOME_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch (e) {
    console.warn('Cache read error:', e);
  }
  return null;
};

const setCache = (data) => {
  try {
    // Create lite version of data for caching
    const liteData = {
      businessItems: {
        phone_numbers: extractLiteArray(data.businessItems?.phone_numbers),
        car_plates: extractLiteArray(data.businessItems?.car_plates),
        auto: extractLiteArray(data.businessItems?.auto),
        real_estate: extractLiteArray(data.businessItems?.real_estate),
        ecommerce: extractLiteArray(data.businessItems?.ecommerce)
      },
      ecommerceData: {
        new_arrivals: extractLiteArray(data.ecommerceData?.new_arrivals),
        discounted: extractLiteArray(data.ecommerceData?.discounted),
        bestsellers: extractLiteArray(data.ecommerceData?.bestsellers)
      },
      carsPriceDrops: extractLiteArray(data.carsPriceDrops),
      discountedItems: {
        phone_numbers: extractLiteArray(data.discountedItems?.phone_numbers),
        car_plates: extractLiteArray(data.discountedItems?.car_plates),
        auto: extractLiteArray(data.discountedItems?.auto),
        real_estate: extractLiteArray(data.discountedItems?.real_estate),
        ecommerce: extractLiteArray(data.discountedItems?.ecommerce),
        all: extractLiteArray(data.discountedItems?.all)
      }
    };
    
    const cacheStr = JSON.stringify({ data: liteData, timestamp: Date.now() });
    // Only cache if size is reasonable (< 500KB)
    if (cacheStr.length < 500000) {
      localStorage.setItem(HOME_CACHE_KEY, cacheStr);
    } else {
      console.warn('Cache too large, skipping:', (cacheStr.length / 1024).toFixed(1) + 'KB');
    }
  } catch (e) {
    console.warn('Cache write error:', e);
  }
};

export const useHomeData = () => {
  const [latestItems, setLatestItems] = useState({
    numbers: [],
    cars: [],
    real_estate: [],
    spare_parts: [],
    products: []
  });
  const [storeSections, setStoreSections] = useState({
    new_arrivals: [],
    on_sale: [],
    bestsellers: []
  });
  const [marketplaceSections, setMarketplaceSections] = useState({
    cars: { new: [], price_updated: [] },
    real_estate: { new: [], price_updated: [] },
    numbers: { new: [], price_updated: [] },
    plates: { new: [], price_updated: [] }
  });
  const [businessItems, setBusinessItems] = useState({
    phone_numbers: [],
    car_plates: [],
    auto: [],
    real_estate: [],
    ecommerce: []
  });
  const [discountedItems, setDiscountedItems] = useState({
    phone_numbers: [],
    car_plates: [],
    auto: [],
    real_estate: [],
    ecommerce: [],
    all: []
  });
  const [carsPriceDrops, setCarsPriceDrops] = useState([]);
  const [ecommerceData, setEcommerceData] = useState({
    new_arrivals: [],
    discounted: [],
    bestsellers: []
  });
  const [realEstateData, setRealEstateData] = useState({
    for_sale: [],
    for_rent: [],
    for_investment: [],
    price_updated: []
  });
  const [businessStores, setBusinessStores] = useState([]);
  const [personalNumbers, setPersonalNumbers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Apply cached data to state
  const applyCachedData = useCallback((cached) => {
    if (cached.businessItems) setBusinessItems(cached.businessItems);
    if (cached.ecommerceData) setEcommerceData(cached.ecommerceData);
    if (cached.carsPriceDrops) setCarsPriceDrops(cached.carsPriceDrops);
    if (cached.discountedItems) setDiscountedItems(cached.discountedItems);
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    // Try to show cached data immediately
    const cached = getCache();
    if (cached && isMounted) {
      applyCachedData(cached);
      setLoading(false);
    }

    const fetchData = async () => {
      try {
        // Customer backend is now the single source of truth for the homepage
        // business items. It aggregates from cloudgm_business.store_items with
        // parallel queries + batched store lookups → ~1-2s response.
        //
        // The merchant-direct fetch was removed because it downloads the raw
        // base64 image blobs (40MB+ for the `auto` category) which was making
        // the homepage take 15-30s to paint on production.
        const withTimeout = (p) => p.catch(() => ({ data: { success: false } }));

        // Fetch the customer backend aggregation with a generous ceiling that
        // still accommodates cold Atlas connections but doesn't hang forever.
        const homepageRes = await withTimeout(
          axios.get(`${BACKEND_URL}/api/customer/homepage/all`, { timeout: 20000 })
        );
        
        if (!isMounted) return;
        
        let newBusinessItems = businessItems;
        let newEcommerceData = ecommerceData;
        let newCarsPriceDrops = carsPriceDrops;
        let newDiscountedItems = discountedItems;
        
        if (homepageRes.data.success) {
          const data = homepageRes.data;
          
          newBusinessItems = {
            phone_numbers: normalizeArray(data.phone_numbers),
            car_plates:    normalizeArray(data.car_plates),
            auto:          normalizeArray(data.cars),
            real_estate:   normalizeArray(data.real_estate),
            ecommerce:     normalizeArray(data.ecommerce?.new_arrivals),
          };
          setBusinessItems(newBusinessItems);
          
          newEcommerceData = {
            new_arrivals: normalizeArray(data.ecommerce?.new_arrivals),
            discounted:   normalizeArray(data.ecommerce?.discounted).filter(isRealDiscount),
            bestsellers:  normalizeArray(data.ecommerce?.bestsellers),
          };
          setEcommerceData(newEcommerceData);
          
          newCarsPriceDrops = normalizeArray(data.cars_price_drops);
          setCarsPriceDrops(newCarsPriceDrops);
          
          // Strict discount filter: never surface items in "offers" unless they
          // truly have a discount.
          const allDisc = normalizeArray(data.all_discounted).filter(isRealDiscount);
          newDiscountedItems = {
            phone_numbers: allDisc.filter(i => i.store_type === 'phone_numbers'),
            car_plates:    allDisc.filter(i => i.store_type === 'car_plates'),
            auto:          allDisc.filter(i => i.store_type === 'auto'),
            real_estate:   allDisc.filter(i => i.store_type === 'real_estate'),
            ecommerce:     newEcommerceData.discounted,
            all:           allDisc,
          };
          setDiscountedItems(newDiscountedItems);
        }
        
        // Set loading to false after main data is loaded
        if (isMounted) setLoading(false);
        
        // Cache the main data (lite version)
        setCache({
          businessItems: newBusinessItems,
          ecommerceData: newEcommerceData,
          carsPriceDrops: newCarsPriceDrops,
          discountedItems: newDiscountedItems
        });
        
        // Fetch secondary data in parallel (less critical). Cap each at 6s so
        // an occasional slow endpoint doesn't hold everything else back.
        const [marketplaceRes, storeRes, sectionsRes, personalRes, realEstateRes] = await Promise.all([
          withTimeout(axios.get(`${BACKEND_URL}/api/marketplace/latest?limit=6`, { timeout: 6000 })),
          withTimeout(axios.get(`${BACKEND_URL}/api/products/store-sections`,   { timeout: 6000 })),
          withTimeout(axios.get(`${BACKEND_URL}/api/marketplace/sections`,      { timeout: 6000 })),
          withTimeout(axios.get(`${BACKEND_URL}/api/public/personal-numbers?limit=20`, { timeout: 6000 })),
          withTimeout(axios.get(`${BACKEND_URL}/api/customer/real-estate/home-sections`, { timeout: 6000 })),
        ]);
        
        if (!isMounted) return;
        
        if (realEstateRes.data.success) {
          setRealEstateData({
            for_sale:       normalizeArray(realEstateRes.data.for_sale),
            for_rent:       normalizeArray(realEstateRes.data.for_rent),
            for_investment: normalizeArray(realEstateRes.data.for_investment),
            price_updated:  normalizeArray(realEstateRes.data.price_updated).filter(isRealDiscount),
          });
        }
        
        if (marketplaceRes.data.success) {
          const d = marketplaceRes.data.data || {};
          setLatestItems({
            numbers:      normalizeArray(d.numbers),
            cars:         normalizeArray(d.cars),
            real_estate:  normalizeArray(d.real_estate),
            spare_parts:  normalizeArray(d.spare_parts),
            products:     normalizeArray(d.products),
          });
        }
        if (storeRes.data.success) {
          const d = storeRes.data.data || {};
          setStoreSections({
            new_arrivals: normalizeArray(d.new_arrivals),
            on_sale:      normalizeArray(d.on_sale).filter(isRealDiscount),
            bestsellers:  normalizeArray(d.bestsellers),
          });
        }
        if (sectionsRes.data.success) {
          const d = sectionsRes.data.data || {};
          const cleanBucket = (b) => ({
            new:           normalizeArray(b?.new),
            price_updated: normalizeArray(b?.price_updated).filter(isRealDiscount),
          });
          setMarketplaceSections({
            cars:        cleanBucket(d.cars),
            real_estate: cleanBucket(d.real_estate),
            numbers:     cleanBucket(d.numbers),
            plates:      cleanBucket(d.plates),
          });
        }
        if (personalRes.data.success && personalRes.data.items) {
          setPersonalNumbers(normalizeArray(personalRes.data.items));
        }
        
      } catch (error) {
        console.error('Error fetching home data:', error);
        if (isMounted) setLoading(false);
      }
    };
    
    fetchData();
    
    return () => { isMounted = false; };
  }, []);

  return {
    latestItems,
    storeSections,
    marketplaceSections,
    businessItems,
    discountedItems,
    carsPriceDrops,
    ecommerceData,
    realEstateData,
    businessStores,
    personalNumbers,
    loading
  };
};

export const useFavorites = (user) => {
  const [favorites, setFavorites] = useState([]);

  const fetchFavorites = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data.data || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user, fetchFavorites]);

  const isFavorite = (itemId) => {
    return favorites.some(fav => fav.item_id === itemId);
  };

  return { favorites, fetchFavorites, isFavorite };
};

export default useHomeData;
